import React from 'react';
import cn from 'classnames';
import { getFields } from 'types';
import { ensureNonEmptyItems, typedComponentProps } from 'utils';
import { DocumentKey } from 'containers/worker/utils';
import {
  FormInputTypes,
  FormInput,
  UseInputs,
  UseFormMethods,
  GridColSpan,
} from 'components/form';
import { AcknowledgmentProgress } from 'components/acknowledgmentProgress';
import {
  JobsiteWorkerDocumentArgs,
  JobsiteWorkerDocumentsData,
  generateJobsiteWorkerDocument,
  getDocumentPreviewUrl,
} from 'containers/workerOnboarding/helpers/forms';
import {
  getVisibleDocuments,
  getStepFieldsConfig,
  getRequiredDocuments,
} from 'containers/workerOnboarding/helpers/utils';
import {
  OnboardingStepKey,
  SiteSafetyTrainingDataType,
  OnboardingStepProps,
  JobsiteWorker,
  JobsiteWorkerDocumentType,
} from 'containers/workerOnboarding/types';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import { dateValidation } from 'utils/validation';
import { useDidUpdateEffect } from '@odin-labs/components';
import { VideoAcknowledgmentStatus } from 'components/videoAcknowledgmentStatus';

type GetFormInputsArgs = Pick<
  OnboardingStepProps,
  | 'loading'
  | 'jobsiteWorker'
  | 'jobsiteWorkerDocumentTypes'
  | 'filteredDocumentTypeIds'
  | 'documents'
  | 'defaultFormValues'
>;

const useAcknowledgmentProgressUpdate = (
  documentKey: DocumentKey,
  form: UseFormMethods<SiteSafetyTrainingDataType>,
): void => {
  const { watch, setValue } = form;
  const frontFile = watch(`${documentKey}-front`);

  useDidUpdateEffect(() => {
    const value = frontFile ? 1 : 0;
    setValue(
      `${documentKey}-acknowledgment-progress-fraction`,
      value.toString(),
    );
  }, [frontFile]);
};

export const getFormInputsHook =
  (args: GetFormInputsArgs): UseInputs<SiteSafetyTrainingDataType> =>
  (
    form: UseFormMethods<SiteSafetyTrainingDataType>,
  ): FormInput<SiteSafetyTrainingDataType>[] => {
    const {
      loading,
      jobsiteWorker,
      jobsiteWorkerDocumentTypes,
      filteredDocumentTypeIds,
      documents,
      defaultFormValues,
    } = args;
    const { dirtyFields } = form.formState ?? {};

    useAcknowledgmentProgressUpdate(DocumentKey.JobsiteSafetyDocument, form);

    return React.useMemo(() => {
      const fieldsConfig = getStepFieldsConfig(
        OnboardingStepKey.SiteSpecificOrientation,
        jobsiteWorker,
      );

      const jobsiteConfigWorkerInfo =
        getFields<
          EditJobsiteConfigurationFormData['siteSpecificOrientation']
        >();
      const isStickerNumberRequired =
        fieldsConfig[jobsiteConfigWorkerInfo.stickerNumber]?.isRequired ??
        false;
      const isStickerNumberHidden =
        fieldsConfig[jobsiteConfigWorkerInfo.stickerNumber]?.isHidden ?? false;

      const visibleDocuments = getVisibleDocuments(jobsiteWorker);
      const requiredDocuments = getRequiredDocuments(jobsiteWorker);

      const generateJobsiteWorkerDocumentIfAssigned = (
        docArgs: JobsiteWorkerDocumentArgs<SiteSafetyTrainingDataType>,
      ): FormInput<SiteSafetyTrainingDataType> | undefined => {
        return (
          filteredDocumentTypeIds[docArgs.documentKey] &&
          visibleDocuments?.includes(docArgs.documentKey) &&
          generateJobsiteWorkerDocument({
            ...docArgs,
            suppressRequiredWhenDocIsNotDirty: true,
            dirtyFields,
          })
        );
      };

      const withFilesDocumentIds = Object.fromEntries(
        documents
          .filter(({ key: doc }) =>
            defaultFormValues.some(
              ({ name: fieldName, value }) =>
                value &&
                [`${doc}-front`, `${doc}-back`].includes(fieldName as string),
            ),
          )
          .map(({ key, id }) => [key, id]),
      );

      const getPreviewUrl = (documentKey: string): string => {
        return getDocumentPreviewUrl(withFilesDocumentIds[documentKey]);
      };
      const visibleOnboardingVideos = (jobsiteWorkerDocumentTypes ?? []).filter(
        (dt) =>
          dt.workerDocumentType.key === DocumentKey.JobsiteSafetyVideo &&
          visibleDocuments.includes(dt.key),
      );

      return ensureNonEmptyItems<FormInput<SiteSafetyTrainingDataType>>([
        !loading &&
          !isStickerNumberHidden && {
            name: 'stickerNumberSection',
            element: FormInputTypes.Section,
            elementProps: {
              title: 'Helmet sticker number',
              childrenGridColumns: '1',
              details:
                "The number your team prints on the worker's helmet sticker. For tracking only, not linked to Odin access.",
            },
            layout: 'odin-border-t odin-border-gray-200',
            children: [
              {
                name: 'stickerNumber',
                element: FormInputTypes.Field,
                validation: { required: isStickerNumberRequired },
                layout: cn('md:odin-w-1/2 lg:odin-w-1/3'),
              },
            ],
          },
        generateJobsiteWorkerDocumentIfAssigned({
          documentKey: DocumentKey.SiteSpecificOrientation,
          label: 'Site Specific Orientation',
          getPreviewUrl,
        }),
        !!visibleOnboardingVideos.length && {
          name: 'onboardingVideosSection',
          element: FormInputTypes.Section,
          layout: 'odin-border-t odin-border-gray-200',
          skipChildrenNamePrefix: true,
          elementProps: {
            childrenGridColumns: '1',
            title: 'Onboarding Videos',
            details: `Review the worker’s progress on each onboarding video.
              Acknowledge on their behalf if needed.`,
          },
          children: visibleOnboardingVideos
            .sort((a, b) => a.position - b.position)
            .map((dt) => {
              const isVideoRequired = !!requiredDocuments?.includes(dt.key);
              return generateJobsiteWorkerDocumentIfAssigned({
                documentKey: dt.key,
                label: dt.name,
                hideLabel: true,
                className: cn('!odin-p-0'),
                getPreviewUrl,
                withoutFiles: true,
                requiredFields: [],
                children: {
                  stickerNumber: undefined,
                  [`${dt.key}-acknowledgment-status`]: {
                    element: FormInputTypes.CustomInput,
                    elementProps: {
                      customInput: VideoAcknowledgmentStatus,
                      ...typedComponentProps<typeof VideoAcknowledgmentStatus>({
                        loading,
                        jobsiteWorker,
                        documentType: dt,
                        isVideoRequired,
                        acknowledgedBy: documents.find(
                          (doc) => doc.key === dt.key,
                        )?.acknowledgedBy,
                      }),
                    },
                    validation: {
                      validate: (data: boolean) => {
                        if (
                          !data &&
                          isVideoRequired &&
                          !jobsiteWorker?.siteSpecificTrainingCompletedAt
                        ) {
                          return 'Onboarding video is required but incomplete.';
                        }
                        return true;
                      },
                    },
                    layout: [GridColSpan.SpanFull],
                  },
                  [`${dt.key}-acknowledged-by-user-id`]: {
                    element: FormInputTypes.Field,
                    label: 'Acknowledged by user ID',
                    elementProps: {
                      disabled: true,
                    },
                    layout: [
                      'odin-hidden',
                      GridColSpan.SpanFull,
                      GridColSpan.SmSpan6,
                    ],
                  },
                  [`${dt.key}-acknowledgment-date`]: {
                    element: FormInputTypes.Field,
                    label: 'Acknowledgment date',
                    elementProps: {
                      placeholder: 'mm/dd/yyyy',
                      fieldType: 'date',
                      disabled: true,
                    },
                    validation: { pattern: dateValidation },
                    layout: [
                      'odin-hidden',
                      GridColSpan.SpanFull,
                      GridColSpan.SmSpan6,
                    ],
                  },
                },
              });
            }),
        },
        generateJobsiteWorkerDocumentIfAssigned({
          documentKey: DocumentKey.JobsiteSafetyDocument,
          label: 'Worker Safety Document Acknowledgment',
          getPreviewUrl,
          requiredFields: ['front'],
          children: {
            stickerNumber: undefined,
            [`${DocumentKey.JobsiteSafetyDocument}-acknowledgment-status`]: {
              element: FormInputTypes.Toggle,
              label: 'Worker has read the document',
              elementProps: {
                toggleDescription: 'Worker completed in self-onboarding',
                toggleAlignment: 'right-with-space',
                disabled: true,
              },
              layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
            },
            [`${DocumentKey.JobsiteSafetyDocument}-acknowledgment-progress-fraction`]:
              {
                element: FormInputTypes.CustomInput,
                label: 'Acknowledgment progress',
                elementProps: { customInput: AcknowledgmentProgress },
                layout: 'odin-hidden',
              },
            [`${DocumentKey.JobsiteSafetyDocument}-acknowledgment-date`]: {
              element: FormInputTypes.Field,
              label: 'Acknowledgment date',
              elementProps: {
                placeholder: 'mm/dd/yyyy',
                fieldType: 'date',
              },
              validation: { pattern: dateValidation, required: true },
              layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
            },
          },
        }),
        generateJobsiteWorkerDocumentIfAssigned({
          documentKey: DocumentKey.WorkerConsentDocument,
          label: 'Worker Consent Form',
          getPreviewUrl,
          requiredFields: ['front'],
          children: {
            stickerNumber: undefined,
            [`${DocumentKey.WorkerConsentDocument}-acknowledgment-status`]: {
              element: FormInputTypes.Toggle,
              label: 'Worker has read the document',
              elementProps: {
                toggleDescription: 'Worker completed in self-onboarding',
                toggleAlignment: 'right-with-space',
                disabled: true,
              },
              layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
            },
            [`${DocumentKey.WorkerConsentDocument}-acknowledgment-date`]: {
              element: FormInputTypes.Field,
              label: 'Acknowledgment date',
              elementProps: {
                placeholder: 'mm/dd/yyyy',
                fieldType: 'date',
              },
              validation: { pattern: dateValidation, required: true },
              layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
            },
          },
        }),
      ]);
    }, [
      jobsiteWorker,
      jobsiteWorkerDocumentTypes,
      filteredDocumentTypeIds,
      documents,
      JSON.stringify(dirtyFields),
      loading,
    ]);
  };

type DefaultFormValue = JobsiteWorkerDocumentsData['defaultFormValues'][number];

export const getMultiInstanceDocTypesByWorkerDocType = (
  jobsiteWorkerDocumentTypes: JobsiteWorkerDocumentType[],
): Record<string, JobsiteWorkerDocumentType[]> => {
  return (
    jobsiteWorkerDocumentTypes
      // only multi-instance document types have a key at jobsiteWorkerDocumentType level
      .filter((dt) => dt.key)
      .reduce<Record<string, JobsiteWorkerDocumentType[]>>((acc, dt) => {
        const { key: wdtKey } = dt.workerDocumentType;
        if (!acc[wdtKey]) acc[wdtKey] = [];
        acc[wdtKey].push(dt);
        return acc;
      }, {} as Record<string, JobsiteWorkerDocumentType[]>)
  );
};
export const getDefaultValues = (
  jobsiteWorker: JobsiteWorker,
  multiInstanceDocTypesByWorkerDocType: Record<
    string,
    JobsiteWorkerDocumentType[]
  >,
  defaultFormValues: DefaultFormValue[],
): DefaultFormValue[] => {
  const jobsiteSafetyVideoInstances =
    multiInstanceDocTypesByWorkerDocType[DocumentKey.JobsiteSafetyVideo] ?? [];
  return Object.entries({
    ...jobsiteSafetyVideoInstances.reduce((acc, dt) => {
      return {
        ...acc,
        [`${dt.key}-acknowledgment-status`]: false,
        [`${dt.key}-acknowledgment-date`]: '',
        [`${dt.key}-acknowledgment-progress-fraction`]: '',
        [`${dt.key}-acknowledged-by-user-id`]: '',
      };
    }, {}),
    [`${DocumentKey.JobsiteSafetyDocument}-acknowledgment-status`]: false,
    [`${DocumentKey.JobsiteSafetyDocument}-acknowledgment-date`]: '',
    [`${DocumentKey.JobsiteSafetyDocument}-acknowledgment-progress-fraction`]:
      '',
    [`${DocumentKey.WorkerConsentDocument}-acknowledgment-status`]: false,
    [`${DocumentKey.WorkerConsentDocument}-acknowledgment-date`]: '',
    ...Object.fromEntries(defaultFormValues.map((dv) => [dv.name, dv.value])),
    stickerNumber: jobsiteWorker?.stickerNumber ?? '',
  }).map(([name, value]) => ({ name, value }));
};
